import {
  CodePipelineClient,
  GetPipelineStateCommand,
  PutApprovalResultCommand,
  StartPipelineExecutionCommand,
  StopPipelineExecutionCommand,
  EnableStageTransitionCommand,
  DisableStageTransitionCommand,
  RetryStageExecutionCommand,
  ListPipelineExecutionsCommand,
} from "@aws-sdk/client-codepipeline";

const PIPELINE_NAME = "pos-system-deploy";
const REGION = "us-east-1";

let client: CodePipelineClient | null = null;

export interface PipelineStageStatus {
  stageName: string;
  status: string;
  inboundTransitionState?: {
    enabled: boolean;
    disabledReason?: string;
  };
  latestExecution?: {
    pipelineExecutionId: string;
    status: string;
  };
  actionStates: {
    actionName: string;
    currentRevision?: { revisionId: string };
    latestExecution?: {
      status: string;
      token?: string;
      lastStatusChange: Date;
      summary?: string;
    };
  }[];
}

export interface PipelineExecution {
  pipelineExecutionId: string;
  status: string;
  startTime: Date;
  lastUpdateTime?: Date;
  sourceRevisions?: { revisionId: string; actionName: string }[];
}

export function configurePipelineClient(credentials: {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken?: string;
}) {
  client = new CodePipelineClient({ region: REGION, credentials });
}

export function getPipelineClient(): CodePipelineClient {
  if (!client) {
    client = new CodePipelineClient({ region: REGION });
  }
  return client;
}

export async function getPipelineState(): Promise<PipelineStageStatus[]> {
  const pipeline = getPipelineClient();
  const response = await pipeline.send(new GetPipelineStateCommand({ name: PIPELINE_NAME }));
  if (!response.stageStates) return [];

  return response.stageStates.map((stage) => ({
    stageName: stage.stageName || "",
    status: stage.latestExecution?.status || "Unknown",
    inboundTransitionState: stage.inboundTransitionState
      ? {
          enabled: stage.inboundTransitionState.enabled !== false,
          disabledReason: stage.inboundTransitionState.disabledReason,
        }
      : undefined,
    latestExecution: stage.latestExecution
      ? {
          pipelineExecutionId: stage.latestExecution.pipelineExecutionId || "",
          status: stage.latestExecution.status || "",
        }
      : undefined,
    actionStates: (stage.actionStates || []).map((action) => ({
      actionName: action.actionName || "",
      currentRevision: action.currentRevision
        ? { revisionId: action.currentRevision.revisionId || "" }
        : undefined,
      latestExecution: action.latestExecution
        ? {
            status: action.latestExecution.status || "",
            token: action.latestExecution.token,
            lastStatusChange: action.latestExecution.lastStatusChange || new Date(),
            summary: action.latestExecution.summary,
          }
        : undefined,
    })),
  }));
}

/** Get recent pipeline executions */
export async function getRecentExecutions(maxResults = 10): Promise<PipelineExecution[]> {
  const pipeline = getPipelineClient();
  const response = await pipeline.send(
    new ListPipelineExecutionsCommand({
      pipelineName: PIPELINE_NAME,
      maxResults,
    })
  );

  return (response.pipelineExecutionSummaries || []).map((exec) => ({
    pipelineExecutionId: exec.pipelineExecutionId || "",
    status: exec.status || "",
    startTime: exec.startTime || new Date(),
    lastUpdateTime: exec.lastUpdateTime,
    sourceRevisions: exec.sourceRevisions?.map((r) => ({
      revisionId: r.revisionId || "",
      actionName: r.actionName || "",
    })),
  }));
}

/**
 * Trigger the pipeline with a specific source commit.
 * Uses sourceRevisions to override what CodeCommit provides.
 */
export async function triggerPipelineWithVersion(commitId?: string): Promise<string> {
  const pipeline = getPipelineClient();

  const params: any = { name: PIPELINE_NAME };

  // If a specific commit is provided, use source revision overrides
  if (commitId) {
    params.sourceRevisions = [
      {
        actionName: "CodeCommit_Source",
        revisionType: "COMMIT_ID",
        revisionValue: commitId,
      },
    ];
  }

  const response = await pipeline.send(new StartPipelineExecutionCommand(params));
  return response.pipelineExecutionId || "";
}

/** Trigger pipeline (no specific commit — uses HEAD) */
export async function triggerPipeline(): Promise<string> {
  return triggerPipelineWithVersion();
}

export async function approveStage(
  stageName: string,
  actionName: string,
  token: string,
  summary?: string
): Promise<void> {
  const pipeline = getPipelineClient();
  await pipeline.send(
    new PutApprovalResultCommand({
      pipelineName: PIPELINE_NAME,
      stageName,
      actionName,
      token,
      result: { summary: summary || "Approved via DevPortal", status: "Approved" },
    })
  );
}

export async function rejectStage(
  stageName: string,
  actionName: string,
  token: string,
  summary?: string
): Promise<void> {
  const pipeline = getPipelineClient();
  await pipeline.send(
    new PutApprovalResultCommand({
      pipelineName: PIPELINE_NAME,
      stageName,
      actionName,
      token,
      result: { summary: summary || "Rejected via DevPortal", status: "Rejected" },
    })
  );
}

export async function enableGate(stageName: string): Promise<void> {
  const pipeline = getPipelineClient();
  await pipeline.send(
    new EnableStageTransitionCommand({
      pipelineName: PIPELINE_NAME,
      stageName,
      transitionType: "Inbound",
    })
  );
}

export async function disableGate(stageName: string, reason?: string): Promise<void> {
  const pipeline = getPipelineClient();
  await pipeline.send(
    new DisableStageTransitionCommand({
      pipelineName: PIPELINE_NAME,
      stageName,
      transitionType: "Inbound",
      reason: reason || "Disabled via DevPortal",
    })
  );
}

export async function retryStage(stageName: string, executionId: string): Promise<void> {
  const pipeline = getPipelineClient();
  await pipeline.send(
    new RetryStageExecutionCommand({
      pipelineName: PIPELINE_NAME,
      stageName,
      pipelineExecutionId: executionId,
      retryMode: "FAILED_ACTIONS",
    })
  );
}

export async function stopPipeline(executionId: string, reason?: string): Promise<void> {
  const pipeline = getPipelineClient();
  await pipeline.send(
    new StopPipelineExecutionCommand({
      pipelineName: PIPELINE_NAME,
      pipelineExecutionId: executionId,
      abandon: true,
      reason: reason || "Rolled back via DevPortal",
    })
  );
}
